import React, { useEffect } from 'react';
// libraries
import { isEmpty } from 'lodash';
import PieSocket from 'piesocket-js';
// redux
import { dispatch, useSelector } from '../../redux/store';
import { getNotificationsJwt, setPersistedEmailNotifications, updatePersistedEmailNotification, bumpNotificationsRefresh, recentToastKeys, shownNotifIds, shownWhatsappCallToastIds } from '../../redux/slices/liveNotificationSlice';
import { clearFollowUpTableMoved } from '../../redux/slices/lead';
// routes
import { PATH_DASHBOARD } from '../../routes/paths';
import { normalizeActionLink } from '../../layouts/dashboard/header/NotificationsPopover';
// hooks
import { useAuth, useGetCurrentUser } from '../../hooks';
// notifications
import { useNotifications } from '../../sections/notifications/NotificationProvider';
import { pieRoom } from '../../utils/piesocketRoom';
import useVoiceCredential from '../../sections/notifications/useVoiceCredential';
import {
    VOICE_CALL_EVENT, VOICE_CALL_TYPE, VOICE_CALL_WINDOW_EVENT, isForAnotherPractice, voiceCallToastFor,
} from '../../sections/notifications/voiceCallScreenPop';
import { isCallPillEnabled } from '../callPill/callPillFlag';
import { isPillPayload } from '../callPill/callPillModel';
import { isCallVerb } from '../../sections/notifications/callNotification';
import { patientPath } from '../callPill/patientPath';

// ── Map verb to a friendly action label for the iOS toast CTA ──
function getActionLabel(verb) {
    if (verb === 'new_email_reply_received') return 'Open Email';
    if (['appointment_rescheduled', 'appointment_cancelled', 'calcom_sync_failed'].includes(verb)) return 'View appointment';
    if (verb === 'new_lead') return 'Open Lead';
    if (verb === 'phone_number_clicked') return 'View';
    if (verb === 'gmb_review_created') return 'Open Reviews';
    if (verb === 'lead_follow_up_moved') return 'Open Lead';
    if (verb === 'whatsapp_message' || verb === 'whatsapp_message_received') return 'Open Chat';
    if (verb === 'whatsapp_call_incoming') return 'Open Call';
    if (verb === 'incoming_call' || verb === 'answered_call') return 'Open lead';
    if (verb === 'missed_call') return 'Call back';
    if (verb === 'whatsapp_template_status') return 'View Templates';
    if (verb === 'annotation_changes_completed') return 'View changes';
    if (verb === 'lead_archived') return 'View Lead';
    // Task V2 notification verbs
    if (verb === 'task_assigned') return 'Open Task';
    if (verb === 'task_due_soon') return 'Open Task';
    if (verb === 'task_due_now') return 'Open Task';
    if (verb === 'task_overdue') return 'Open Task';
    if (verb === 'task_completed') return 'View Task';
    if (verb === 'task_comment') return 'View Comment';
    if (verb === 'form_submitted') return 'View Submission';
    return 'View';
}

// ── Build an action_link for notifications that lack one ──
function buildActionLink(parsedMessage, practiceId, resolvedVerb) {
    // Check verb-specific routes first (before generic action_link fallback)
    const verb = resolvedVerb || parsedMessage?.verb;
    if (verb === 'annotation_changes_completed') {
        return PATH_DASHBOARD.annotationSessions;
    }
    // Inbound calls: the lead page when the caller is known, else Call Logs.
    if (isCallVerb(verb) && practiceId) {
        const callLeadId = parsedMessage?.data?.lead_id ?? parsedMessage?.data?.leadId;
        return callLeadId ? patientPath(practiceId, callLeadId) : PATH_DASHBOARD.callLogs.practice(practiceId);
    }
    // Task V2 notifications — link to task detail page
    if (verb?.startsWith('task_') && parsedMessage?.data?.task_id && practiceId) {
        return PATH_DASHBOARD.task.tasks.detail(practiceId, parsedMessage.data.task_id);
    }
    if (verb === 'form_submitted') {
        // Compact realtime payloads carry the canonical request highlight here.
        // Their notification/submission ID is not the ConsentRequest ID.
        if (typeof parsedMessage?.action_link === 'string' && parsedMessage.action_link.trim()) {
            return normalizeActionLink(parsedMessage.action_link);
        }
        // Submission ID lives on action_object.id (django-notifications-hq sets the GFK)
        const submissionId = parsedMessage?.action_object?.id
            || parsedMessage?.data?.submissionId
            || parsedMessage?.action_object_object_id;
        return submissionId
            ? `${PATH_DASHBOARD.formSubmissions}?cr=${submissionId}`
            : PATH_DASHBOARD.formSubmissions;
    }
    const threadId = parsedMessage?.thread || parsedMessage?.threadId || parsedMessage?.thread_id;
    if (threadId && practiceId) {
        return PATH_DASHBOARD.whatsapp.thread(practiceId, threadId);
    }
    if (parsedMessage?.action_link) {
        return normalizeActionLink(parsedMessage.action_link);
    }
    return undefined;
}

const LiveNotificationHandler = () => {
    const { isAuthenticated } = useAuth();
    const { notificationsJwt, notificationsPractice, tasksJwt: legacyTasksJwt, tasksRoom: legacyTasksRoom } = useSelector((state) => state.liveNotifications);
    const { user } = useGetCurrentUser();
    const { practiceId } = useSelector((state) => state.practiceTreatmentList);
    const pieSocket = React.useRef(null);
    // Which practiceId the live socket was built for, and the room ids it
    // subscribed to. Used to detect a practice switch and to unsubscribe the
    // OLD practice's rooms (the effect closure's practiceId is already the NEW
    // one by the time teardown runs).
    const socketPracticeRef = React.useRef(null);
    const socketRoomsRef = React.useRef([]);
    // The task room gets its OWN client. A PieSocket token authorises exactly
    // one room (the backend mints it with `sub` = that room), so the task room
    // cannot ride on the notifications socket — that is precisely the bug this
    // second ref exists to prevent: on 23 Sep the notifications client was
    // asked to subscribe to the task room, PieSocket rejected every attempt,
    // and the client's retry loop exhausted the account's connection budget,
    // which the WhatsApp phone sockets share. Live messages died product-wide.
    const taskSocket = React.useRef(null);
    const taskRoomRef = React.useRef(null);
    const taskAuthRef = React.useRef(null);
    // The task client's own practice. It is built from its own token and can
    // outlive the notifications client (that one nulls its ref on a failed
    // subscribe), so its practice has to be tracked on its own too — otherwise
    // a switch cannot tell whether a lone task client is stale.
    const taskPracticeRef = React.useRef(null);
    const userId = user?.user?.id;
    const voiceCredential = useVoiceCredential({ isAuthenticated, userId, practiceId });
    const identity = `${userId}:${practiceId}`;
    // Keep credential provenance separate from socket ownership. Cleanup clears
    // sockets on a switch, but must not make an old JWT look fresh for the new
    // practice when an older backend omitted notificationsPractice.
    const notificationCredentialRef = React.useRef(null);
    const taskCredentialRef = React.useRef(null);
    const selectedIdentityRef = React.useRef(identity);
    const legacyOwnerRef = React.useRef(null);
    const legacyIdentityChangedRef = React.useRef(false);
    const identityReady = Boolean(isAuthenticated && userId && practiceId);
    if (identityReady && legacyOwnerRef.current == null) legacyOwnerRef.current = userId;
    if (legacyOwnerRef.current != null && (!isAuthenticated || (userId && legacyOwnerRef.current !== userId))) {
        legacyIdentityChangedRef.current = true;
    }
    // The legacy slice carries no requesting user. After an in-place account
    // switch a late response cannot safely be assigned to the new account;
    // only the independently identity-bound voice credential is used.
    const legacyOwnerMatches = !legacyIdentityChangedRef.current && legacyOwnerRef.current === userId;
    if (identityReady && legacyOwnerMatches && notificationsJwt && notificationCredentialRef.current?.auth !== notificationsJwt) {
        notificationCredentialRef.current = {
            auth: notificationsJwt, identity: `${userId}:${notificationsPractice ?? practiceId}`,
        };
    }
    if (identityReady && legacyOwnerMatches && legacyTasksJwt && taskCredentialRef.current?.auth !== legacyTasksJwt) {
        taskCredentialRef.current = {
            auth: legacyTasksJwt, identity: `${userId}:${notificationsPractice ?? practiceId}`,
        };
    }
    const notificationContextMatches = identityReady && legacyOwnerMatches && notificationCredentialRef.current?.identity === identity
        && (notificationsPractice == null || String(notificationsPractice) === String(practiceId));
    const taskContextMatches = identityReady && legacyOwnerMatches && taskCredentialRef.current?.identity === identity
        && (notificationsPractice == null || String(notificationsPractice) === String(practiceId));
    // One task socket owns the room, even when both endpoints return it.
    const tasksJwt = voiceCredential?.auth || (taskContextMatches ? legacyTasksJwt : null);
    const tasksRoom = voiceCredential?.room || (taskContextMatches ? legacyTasksRoom : null);
    const { followUpTableMoved } = useSelector((state) => state.lead);
    const { showNotification } = useNotifications();
    // Keep a stable ref so the PieSocket closure always calls the latest function
    const showNotificationRef = React.useRef(showNotification);
    showNotificationRef.current = showNotification;

    // Tear down the live socket: unsubscribe every room it subscribed to and
    // null the refs. PieSocket.unsubscribe(roomId) closes the underlying
    // websocket and removes the channel — mirrors the teardown pattern in
    // layouts/dashboard/index.jsx. Stable identity (refs only) so the unmount
    // cleanup below fires only on true unmount. Guards double-teardown via the
    // null-check, so calling it twice (re-run + unmount) is safe.
    const teardownSocket = React.useCallback(() => {
        // The task socket is torn down first and unconditionally: it can exist
        // while the notifications socket does not (different tokens, different
        // arrival times), and an early return that skipped it would leave an
        // orphan connection open across a practice switch.
        const tasks = taskSocket.current;
        if (tasks) {
            try { tasks.unsubscribe(taskRoomRef.current); } catch (_) { /* already closed — ignore */ }
            taskSocket.current = null;
            taskRoomRef.current = null;
            taskAuthRef.current = null;
        }
        taskPracticeRef.current = null;
        const socket = pieSocket.current;
        if (socket) {
            (socketRoomsRef.current || []).forEach((roomId) => {
                try { socket.unsubscribe(roomId); } catch (_) { /* already closed — ignore */ }
            });
            pieSocket.current = null;
        }
        // Cleared unconditionally, outside the socket check: the ownership
        // bookkeeping must end up empty on every path, including the one where
        // the notifications client was already gone and only the task client
        // was left to close.
        socketRoomsRef.current = [];
        socketPracticeRef.current = null;
    }, []);

    useEffect(() => {
        if (isAuthenticated && user?.id && pieSocket?.current === null) {
            dispatch(getNotificationsJwt({ userId: user.id }));
        }
    }, [isAuthenticated, user]);

    useEffect(() => {
        if (!isAuthenticated || !userId || !practiceId) {
            teardownSocket();
            return undefined;
        }

        // The identity marker survives socket cleanup so every selection
        // change requests fresh generic credentials, including older payloads
        // without notificationsPractice. Voice credentials remain independent.
        const identityChanged = selectedIdentityRef.current !== identity;
        selectedIdentityRef.current = identity;
        if (identityChanged || (notificationsPractice != null && String(notificationsPractice) !== String(practiceId))) {
            dispatch(getNotificationsJwt({ userId }));
        }

        // Two sockets, two independent decisions. The task credential arrives
        // in the same response as the notifications one but through a second
        // dispatch, so this effect can legitimately re-run with the
        // notifications socket already live and the task socket still missing.
        // The old single `if (pieSocket.current !== null) return` swallowed
        // exactly that case, which would have left the task room permanently
        // unopened while looking, from every log, like a clean start.
        // A password-version or transport-mode change can rotate the private
        // room independently of practice selection.
        if (taskSocket.current && tasksRoom && (taskRoomRef.current !== pieRoom(tasksRoom) || taskAuthRef.current !== tasksJwt)) {
            try { taskSocket.current.unsubscribe(taskRoomRef.current); } catch (_) { /* already closed */ }
            taskSocket.current = null;
            taskRoomRef.current = null;
            taskAuthRef.current = null;
            taskPracticeRef.current = null;
        }
        const needsNotificationSocket = Boolean(notificationsJwt && notificationContextMatches && pieSocket.current === null);
        const needsTaskSocket = Boolean(tasksJwt && tasksRoom && taskSocket.current === null);
        if (!needsNotificationSocket && !needsTaskSocket) return undefined;

        {
            // Validate env vars before connecting — undefined values cause silent
            // connection failures (Sentry: REACT-36, 7 users, 239 events).
            const clusterId = import.meta.env.VITE_REACT_APP_SOCKET_CLUSTER_KEY;
            const apiKey = import.meta.env.VITE_REACT_APP_SOCKET_API_KEY;
            if (!clusterId || !apiKey) {
                console.warn('[PieSocket] Missing VITE_REACT_APP_SOCKET_CLUSTER_KEY or VITE_REACT_APP_SOCKET_API_KEY — skipping WebSocket connection');
                return undefined;
            }
            const notificationsRoom = pieRoom(`${practiceId}_notifications`);
            // Voice integration screen-pop: a `voice.call` event (own PieSocket
            // event name, or riding `new-notification` with type='voice.call')
            // pops an urgent toast for an inbound ringing call. Not persisted to
            // the bell — it is transient by nature.
            const handleVoiceCall = (data) => {
                try {
                    if (isForAnotherPractice(data, practiceId)) return;
                    // The call pill (CallPillHost) replaces the ringing toast for
                    // backends that send version-2 live events. Older payloads,
                    // or the pill switched off, keep the toast.
                    const toast = isCallPillEnabled() && isPillPayload(data) ? null : voiceCallToastFor(data, practiceId);
                    if (toast) showNotificationRef.current(toast);
                    // Let open pages (Call Logs) refresh without re-subscribing.
                    try { window.dispatchEvent(new CustomEvent(VOICE_CALL_WINDOW_EVENT, { detail: data })); } catch { /* ignore */ }
                } catch (err) {
                    console.warn('[PieSocket] voice-call payload ignored:', err);
                }
            };
            const handleNotification = (data) => {
                if (!isEmpty(data)) {
                    const parsedMessage = JSON.parse(data);
                    if (parsedMessage?.type === VOICE_CALL_TYPE) { handleVoiceCall(parsedMessage); return; }
                    const recipientId = parsedMessage?.recipient_id || parsedMessage?.recipient;
                    if (recipientId && recipientId !== userId) return;

                    // Inbound calls live in the call pill (a toast would duplicate it). The socket
                    // only names the bell row (the room is practice-wide, so no caller details):
                    // the bell fetches it.
                    if (isCallVerb(parsedMessage?.verb)) {
                        dispatch(bumpNotificationsRefresh());
                        return;
                    }

                    // Persist to Redux for the notification panel / bell icon
                    dispatch(setPersistedEmailNotifications(parsedMessage));

                    // Determine the verb — WhatsApp messages may come without one
                    const verb = parsedMessage?.verb
                        || (parsedMessage?.thread || parsedMessage?.threadId || parsedMessage?.thread_id
                            ? 'whatsapp_message_received'
                            : 'new_lead');

                    // Build a resolved action_link (full path for WA, normalised for others)
                    const actionLink = buildActionLink(parsedMessage, practiceId, verb);

                    // WhatsApp toasts are handled by the phone-specific PieSocket
                    // channel in DashboardLayout.  The platform channel uses a
                    // different ID (Django notification ID vs WhatsApp message ID),
                    // so cross-channel dedup can't match.  Skip the toast here to
                    // avoid duplicates — the Redux persist above already updates
                    // the bell icon / notification panel.
                    const isWpVerb = verb === 'whatsapp_message' || verb === 'whatsapp_message_received';
                    if (isWpVerb) return;
                    const isWhatsappCallVerb = verb === 'whatsapp_call_incoming';
                    if (isWhatsappCallVerb) {
                        const callToastId = parsedMessage?.id
                            || parsedMessage?.data?.callId
                            || parsedMessage?.data?.call_id
                            || parsedMessage?.action_object?.id;
                        if (callToastId) {
                            const key = String(callToastId);
                            if (shownWhatsappCallToastIds.has(key)) return;
                            shownWhatsappCallToastIds.add(key);
                        }
                    }

                    // Deduplicate non-WhatsApp verbs — when the frontend already
                    // showed a toast (e.g. follow-up table move) PieSocket may
                    // deliver the same event seconds later.  Skip if the key
                    // was registered by the frontend handler within the last 10s.
                    if (!isWpVerb) {
                        const leadId = parsedMessage?.data?.leadId
                            || parsedMessage?.action_object?.id
                            || parsedMessage?.lead_id
                            || '';
                        const dedupKey = `${verb}_${leadId}`;
                        if (leadId && recentToastKeys.has(dedupKey)) return;
                    }

                    // Deduplicate by notification ID — prevents the same notification
                    // showing twice if PieSocket retries or Firebase foreground onMessage
                    // fires for the same event (e.g. treatment_started, deposit_paid).
                    const isAppointmentChange = ['appointment_rescheduled', 'appointment_cancelled', 'calcom_sync_failed'].includes(verb);
                    const notifId = parsedMessage?.id ? `${isAppointmentChange ? `${verb}:` : ''}${parsedMessage.id}` : '';
                    if (notifId) {
                        if (shownNotifIds.has(notifId)) return;
                        shownNotifIds.add(notifId);
                        // Auto-expire after 30s so the set stays small
                        setTimeout(() => shownNotifIds.delete(notifId), 30000);
                    }

                    // Show the new iOS glass morphism toast (via ref to avoid stale closure)
                    showNotificationRef.current({
                        id: parsedMessage.id || `notif_${Date.now()}`,
                        verb,
                        description: parsedMessage?.description || parsedMessage?.body || 'You have a new notification',
                        priority: parsedMessage?.priority || (isWhatsappCallVerb ? 'urgent' : 'normal'),
                        requires_action: parsedMessage?.requires_action ?? true,
                        action_label: parsedMessage?.action_label || getActionLabel(verb),
                        action_link: actionLink,
                        data: parsedMessage?.data,
                        action_object: parsedMessage?.action_object,
                    });
                }
            };

            // Quiet live-bump for grouped notifications (e.g. the
            // grouped_count on a same-day phone/WhatsApp click
            // notification getting bumped). Backend pushes this
            // INSTEAD of 'new-notification' for subsequent clicks
            // in the same day, so we update the existing row in
            // Redux without showing a fresh toast.
            const handleNotificationUpdated = (data) => {
                try {
                    if (!data) return;
                    const parsed = JSON.parse(data);
                    const recipientId = parsed?.recipient_id || parsed?.recipient;
                    if (recipientId && recipientId !== userId) return;
                    if (!parsed?.id) return;
                    // Call rows change state in place (ringing -> answered / missed), no toast. The
                    // socket only says which row changed (the room is practice-wide, so it carries no
                    // caller details): the bell fetches the row again.
                    if (isCallVerb(parsed.verb)) {
                        dispatch(bumpNotificationsRefresh());
                        return;
                    }
                    dispatch(updatePersistedEmailNotification({
                        id: parsed.id,
                        description: parsed.description,
                        grouped_count: parsed.grouped_count,
                        verb: parsed.verb,
                    }));
                } catch (err) {
                    console.warn('[PieSocket] notification-updated parse failed:', err);
                }
            };

            if (needsNotificationSocket) {
                pieSocket.current = new PieSocket({
                    clusterId,
                    apiKey,
                    jwt: notificationsJwt,
                });
                // Record which practice/rooms this socket owns so a later practice
                // switch can tear down exactly these rooms.
                socketPracticeRef.current = practiceId;
                socketRoomsRef.current = [];
                socketRoomsRef.current.push(notificationsRoom);
                pieSocket.current.subscribe(notificationsRoom).catch((err) => {
                    console.error('[PieSocket] Failed to subscribe:', err);
                    // Reset to null so a future dep change (practice switch, re-login)
                    // can reattempt. No automatic retry within this session — deps
                    // [notificationsJwt, userId, practiceId] don't change on failure.
                    pieSocket.current = null;
                    socketPracticeRef.current = null;
                    socketRoomsRef.current = [];
                }).then((channel) => {
                    if (!channel) return; // Subscription failed (caught above)
                    channel.listen('new-notification', handleNotification);
                    channel.listen('notification-updated', handleNotificationUpdated);
                    channel.listen(VOICE_CALL_EVENT, handleVoiceCall);
                });
            }

            // Per-user task channel (BE commit 3c8ce50c), on its own client with
            // its own token. Two conditions, both load-bearing:
            //
            //   tasksJwt   — without a token minted for THIS room the subscribe
            //                is rejected and PieSocket retries forever. Opening
            //                no room is strictly better than opening one that
            //                cannot authenticate; task notifications are worth
            //                less than every other live update in the product.
            //   tasksRoom  — the room the backend says the token is for. We do
            //                not reconstruct the name here. It was spelled in
            //                two places before (publisher and browser), they
            //                agreed, and the token nobody minted made both
            //                wrong. One source, handed over with the token.
            if (needsTaskSocket) {
                const client = new PieSocket({ clusterId, apiKey, jwt: tasksJwt });
                taskSocket.current = client;
                taskRoomRef.current = pieRoom(tasksRoom);
                taskAuthRef.current = tasksJwt;
                taskPracticeRef.current = practiceId;
                client.subscribe(pieRoom(tasksRoom)).catch(() => {
                    if (taskSocket.current !== client) return;
                    console.warn('[PieSocket] Private task channel subscription failed.');
                    // Drop the client rather than leaving it to retry. Nothing
                    // in this effect's deps changes on failure, so a silent
                    // retry loop would have no exit.
                    try { taskSocket.current?.unsubscribe(pieRoom(tasksRoom)); } catch (_) { /* ignore */ }
                    taskSocket.current = null;
                    taskRoomRef.current = null;
                    taskAuthRef.current = null;
                }).then((channel) => {
                    if (!channel || taskSocket.current !== client) return;
                    channel.listen('new-notification', (data) => {
                        if (taskSocket.current === client) handleNotification(data);
                    });
                });
            }
        }

        return undefined;
    }, [isAuthenticated, identity, notificationsJwt, notificationsPractice, notificationContextMatches, tasksJwt, tasksRoom, userId, practiceId, teardownSocket]);

    // Account/logout/practice changes invalidate pending callbacks as well as
    // existing sockets before the next credentials can be used.
    useEffect(() => () => teardownSocket(), [isAuthenticated, userId, practiceId, teardownSocket]);

    // Final teardown on unmount (logout / route away). teardownSocket has a
    // stable identity (refs only), so this cleanup fires only on true unmount,
    // not on every dep-change re-run — and its null-guard makes a double call
    // (already torn down by a practice-switch) a no-op.
    useEffect(() => () => teardownSocket(), [teardownSocket]);

    // ── Follow-up table move toast (frontend-only detection) ──
    // When addNotes detects that contacted_count crossed a table threshold
    // (e.g. 0→1 means New → Follow up 1), it sets followUpTableMoved in Redux.
    // We pick it up here and fire an iOS toast, then clear the flag.
    useEffect(() => {
        if (followUpTableMoved) {
            // Register dedup key so the PieSocket duplicate (if any) is skipped
            const dedupKey = `lead_follow_up_moved_${followUpTableMoved.leadId}`;
            recentToastKeys.add(dedupKey);
            setTimeout(() => recentToastKeys.delete(dedupKey), 10000);

            // Enhanced description — include "Note added" when the move was
            // triggered by addNotes so the user gets one combined toast instead
            // of a separate snackbar + toast.
            const description = followUpTableMoved.noteAdded
                ? `Note added — ${followUpTableMoved.leadName} moved to ${followUpTableMoved.newLabel}`
                : `${followUpTableMoved.leadName} moved to ${followUpTableMoved.newLabel}`;

            showNotificationRef.current({
                id: `table_move_${followUpTableMoved.leadId}_${Date.now()}`,
                verb: 'lead_follow_up_moved',
                description,
                priority: 'normal',
                requires_action: true,
                action_label: 'Open Lead',
                action_link: practiceId
                    ? `/dashboard/practice/${practiceId}/lead-detail/${followUpTableMoved.leadId}`
                    : undefined,
                data: { leadId: followUpTableMoved.leadId },
            });
            dispatch(clearFollowUpTableMoved());
        }
    }, [followUpTableMoved, practiceId]);

    // All rendering is handled by NotificationProvider / NotificationToastContainer
    return null;
};

export default LiveNotificationHandler;
